import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from '@mui/material';
import { Save, Delete, Settings, Edit, Add } from '@mui/icons-material';
import { leaveApi } from '../../../api/leaveApi';
import { LeaveTypeDto, SaveLeaveTypeConfiguration } from '../../../types';
import { toast } from 'react-toastify';

const getApiErrorMessage = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (data?.errors && typeof data.errors === 'object') {
    const firstEntry = Object.values(data.errors).find(
      (value) => Array.isArray(value) && value.length > 0
    ) as string[] | undefined;
    if (firstEntry && typeof firstEntry[0] === 'string' && firstEntry[0].trim()) return firstEntry[0];
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
};

// ─── Add Leave Type Dialog ───────────────────────────────────────────────────
const AddLeaveTypeDialog: React.FC<{ open: boolean; onClose: () => void; onSaved: () => void }> = ({
  open,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const [carryForward, setCarryForward] = useState(false);
  const [carryForwardLimit, setCarryForwardLimit] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleMaxDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') { setMaxDays(''); return; }
    const num = Number(value);
    if (num > 365) toast.error('Maximum allowed is 365 days');
    else if (num < 1) toast.error('Minimum value is 1');
    setMaxDays(value);
  };

  const resetForm = () => {
    setName('');
    setMaxDays('');
    setCarryForward(false);
    setCarryForwardLimit('');
    setRequiresApproval(true);
    setFormError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!name || !maxDays) {
      const message = 'Please fill all required fields';
      setFormError(message);
      toast.error(message);
      return;
    }
    const num = Number(maxDays);
    if (num < 1) { const m = 'Minimum value is 1'; setFormError(m); toast.error(m); return; }
    if (num > 365) { const m = 'Maximum allowed is 365 days'; setFormError(m); toast.error(m); return; }

    setSubmitting(true);
    try {
      await leaveApi.createLeaveType({
        name,
        maxDaysPerYear: num,
        isCarryForward: carryForward,
        maxCarryForwardDays: Number(carryForwardLimit) || 0,
        isPaidLeave: true,
        allowHalfDay: false,
        includeHolidays: false,
        includeWeekends: false,
        sortOrder: 0,
      });
      toast.success('Leave type saved successfully!');
      resetForm();
      onSaved();
      onClose();
    } catch (err: any) {
      const message = getApiErrorMessage(err, 'Failed to save leave type');
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={{ textTransform: 'none' }}>
      <DialogTitle>Add Leave Type</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a new leave category (e.g., Casual Leave, Sick Leave).
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              label="Leave Name *"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                const regex = /^[a-zA-Z\s]$/;
                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) return;
                if (!regex.test(e.key) || name.length >= 90) e.preventDefault();
              }}
              placeholder="e.g., Casual Leave"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Max Days *"
              type="number"
              fullWidth
              size="small"
              value={maxDays}
              onChange={handleMaxDaysChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                />
              }
              label="Carry Forward"
            />
          </Grid>

          {carryForward && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Carry Forward Limit"
                type="number"
                fullWidth
                size="small"
                value={carryForwardLimit}
                onChange={(e) => setCarryForwardLimit(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresApproval}
                  onChange={(e) => setRequiresApproval(e.target.checked)}
                />
              }
              label="Requires Approval"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Save />}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Saving...' : 'Save Leave Type'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Leave Type Config Table ─────────────────────────────────────────────────
const LeaveTypeConfig: React.FC<{
  leaveTypes: LeaveTypeDto[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}> = ({ leaveTypes, loading, error, onRefresh }) => {
  const [selectedEditType, setSelectedEditType] = useState<LeaveTypeDto | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    maxDaysPerYear: 0,
    isCarryForward: false,
    maxCarryForwardDays: 0,
    isPaidLeave: true,
    allowHalfDay: false,
    isActive: true,
  });

  const [selectedType, setSelectedType] = useState<LeaveTypeDto | null>(null);
  const [openConfig, setOpenConfig] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [form, setForm] = useState<SaveLeaveTypeConfiguration>({
    leaveTypeId: '',
    entitlementDays: 0,
    carryForward: false,
    maxCarryForwardDays: 0,
    allowHalfDay: false,
    allowNegativeBalance: false,
    attachmentRequired: false,
    approvalRequired: true,
    isActive: true,
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) return;
    try {
      await leaveApi.deleteLeaveType(id);
      toast.success('Leave type deleted');
      onRefresh();
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to delete');
      if (msg?.toLowerCase().includes('associated with existing leave records')) {
        toast.error('Cannot delete this leave type because it is associated with existing leave records or entitlements.');
      } else {
        toast.error(msg);
      }
    }
  };

  const openEditDialog = (leaveType: LeaveTypeDto) => {
    setSelectedEditType(leaveType);
    setEditForm({
      name: leaveType.name,
      description: leaveType.description || '',
      maxDaysPerYear: Math.round(leaveType.maxDaysPerYear || 0),
      isCarryForward: leaveType.isCarryForward,
      maxCarryForwardDays: Math.round(leaveType.maxCarryForwardDays || 0),
      isPaidLeave: leaveType.isPaidLeave,
      allowHalfDay: leaveType.allowHalfDay,
      isActive: leaveType.isActive,
    });
    setEditError(null);
    setOpenEdit(true);
  };

  const closeEditDialog = () => { setOpenEdit(false); setSelectedEditType(null); setEditError(null); };

  const handleEditSave = async () => {
    if (!selectedEditType) return;
    const normalizedName = editForm.name.trim();
    if (!normalizedName) { setEditError('Leave name is required.'); return; }
    if (editForm.maxDaysPerYear < 1 || editForm.maxDaysPerYear > 365) {
      setEditError('Max days must be between 1 and 365.'); return;
    }
    if (editForm.isCarryForward && editForm.maxCarryForwardDays < 0) {
      setEditError('Carry forward limit cannot be negative.'); return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await leaveApi.updateLeaveType(selectedEditType.id, {
        id: selectedEditType.id,
        name: normalizedName,
        description: editForm.description.trim() || undefined,
        maxDaysPerYear: Number(editForm.maxDaysPerYear),
        isCarryForward: editForm.isCarryForward,
        maxCarryForwardDays: editForm.isCarryForward ? Number(editForm.maxCarryForwardDays) : 0,
        isPaidLeave: editForm.isPaidLeave,
        allowHalfDay: editForm.allowHalfDay,
        includeHolidays: selectedEditType.includeHolidays,
        includeWeekends: selectedEditType.includeWeekends,
        sortOrder: selectedEditType.sortOrder,
        colorCode: selectedEditType.colorCode || undefined,
        isActive: editForm.isActive,
      });
      toast.success('Leave type updated successfully');
      closeEditDialog();
      onRefresh();
    } catch (err: any) {
      const message = getApiErrorMessage(err, 'Failed to update leave type');
      setEditError(message);
      toast.error(message);
    } finally {
      setEditSaving(false);
    }
  };

  const openConfigDialog = async (leaveType: LeaveTypeDto) => {
    setSelectedType(leaveType);
    setOpenConfig(true);
    setConfigLoading(true);
    try {
      const config = await leaveApi.getLeaveTypeConfiguration(leaveType.id);
      setForm({
        leaveTypeId: leaveType.id,
        entitlementDays: config.entitlementDays,
        carryForward: config.carryForward,
        maxCarryForwardDays: config.maxCarryForwardDays,
        allowHalfDay: config.allowHalfDay,
        allowNegativeBalance: config.allowNegativeBalance,
        attachmentRequired: config.attachmentRequired,
        approvalRequired: config.approvalRequired,
        isActive: config.isActive,
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        setForm({
          leaveTypeId: leaveType.id,
          entitlementDays: Math.round(leaveType.maxDaysPerYear || 0),
          carryForward: leaveType.isCarryForward,
          maxCarryForwardDays: Math.round(leaveType.maxCarryForwardDays || 0),
          allowHalfDay: leaveType.allowHalfDay,
          allowNegativeBalance: false,
          attachmentRequired: false,
          approvalRequired: true,
          isActive: leaveType.isActive,
        });
      } else {
        toast.error(getApiErrorMessage(err, 'Failed to load configuration'));
      }
    } finally {
      setConfigLoading(false);
    }
  };

  const closeConfigDialog = () => { setOpenConfig(false); setSelectedType(null); };

  const handleConfigSave = async () => {
    if (!selectedType) return;
    setConfigSaving(true);
    try {
      await leaveApi.saveLeaveTypeConfiguration(selectedType.id, { ...form, leaveTypeId: selectedType.id });
      toast.success('Leave configuration saved successfully');
      closeConfigDialog();
      onRefresh();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to save configuration'));
    } finally {
      setConfigSaving(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Leave Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="center"><strong>Max Days</strong></TableCell>
                <TableCell align="center"><strong>Paid</strong></TableCell>
                <TableCell align="center"><strong>Carry Forward</strong></TableCell>
                <TableCell align="center"><strong>CF Limit</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaveTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No leave types found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leaveTypes.map((type) => (
                  <TableRow key={type.id} hover>
                    <TableCell>{type.name}</TableCell>
                    <TableCell>{type.description || '-'}</TableCell>
                    <TableCell align="center">{type.maxDaysPerYear}</TableCell>
                    <TableCell align="center">
                      <Chip label={type.isPaidLeave ? 'Paid' : 'Unpaid'} color={type.isPaidLeave ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={type.isCarryForward ? 'Yes' : 'No'} color={type.isCarryForward ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">{type.isCarryForward ? type.maxCarryForwardDays : '-'}</TableCell>
                    <TableCell>
                      <Chip label={type.isActive ? 'Active' : 'Inactive'} color={type.isActive ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Leave Type">
                        <IconButton size="small" color="secondary" onClick={() => openEditDialog(type)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Config">
                        <IconButton size="small" color="primary" onClick={() => openConfigDialog(type)}>
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(type.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Edit Config Dialog ── */}
      <Dialog open={openConfig} onClose={closeConfigDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Leave Type Configuration</DialogTitle>
        <DialogContent>
          {configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Total Leave Days"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.entitlementDays}
                  onChange={(e) => setForm((prev) => ({ ...prev, entitlementDays: Number(e.target.value) || 0 }))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Maximum Carry Forward"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.maxCarryForwardDays}
                  disabled={!form.carryForward}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxCarryForwardDays: Number(e.target.value) || 0 }))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Entitlement Settings
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.carryForward} onChange={(e) => setForm((prev) => ({ ...prev, carryForward: e.target.checked }))} />}
                  label="Carry Forward Allowed"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.allowHalfDay} onChange={(e) => setForm((prev) => ({ ...prev, allowHalfDay: e.target.checked }))} />}
                  label="Allow Half Day"
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">Advanced Settings</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.allowNegativeBalance} onChange={(e) => setForm((prev) => ({ ...prev, allowNegativeBalance: e.target.checked }))} />}
                  label="Allow Negative Balance"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.attachmentRequired} onChange={(e) => setForm((prev) => ({ ...prev, attachmentRequired: e.target.checked }))} />}
                  label="Attachment Required"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.approvalRequired} onChange={(e) => setForm((prev) => ({ ...prev, approvalRequired: e.target.checked }))} />}
                  label="Approval Required"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
                  label="Configuration Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfigDialog} color="inherit" disabled={configSaving}>Cancel</Button>
          <Button
            onClick={handleConfigSave}
            variant="contained"
            startIcon={configSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={configLoading || configSaving}
          >
            {configSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Leave Type Dialog ── */}
      <Dialog open={openEdit} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Leave Type</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {editError && (
              <Grid size={12}>
                <Alert severity="error">{editError}</Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                label="Leave Name"
                fullWidth
                size="small"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Max Days"
                type="number"
                fullWidth
                size="small"
                value={editForm.maxDaysPerYear}
                onChange={(e) => setEditForm((prev) => ({ ...prev, maxDaysPerYear: Number(e.target.value) || 0 }))}
                inputProps={{ min: 1, max: 365 }}
                sx={{ textTransform: 'none' }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={<Switch checked={editForm.isCarryForward} onChange={(e) => setEditForm((prev) => ({ ...prev, isCarryForward: e.target.checked }))} />}
                label="Carry Forward"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Carry Forward Limit"
                type="number"
                fullWidth
                size="small"
                disabled={!editForm.isCarryForward}
                value={editForm.maxCarryForwardDays}
                onChange={(e) => setEditForm((prev) => ({ ...prev, maxCarryForwardDays: Number(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{ textTransform: 'none' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={<Switch checked={editForm.isPaidLeave} onChange={(e) => setEditForm((prev) => ({ ...prev, isPaidLeave: e.target.checked }))} />}
                label="Paid Leave"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={<Switch checked={editForm.allowHalfDay} onChange={(e) => setEditForm((prev) => ({ ...prev, allowHalfDay: e.target.checked }))} />}
                label="Allow Half Day"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={<Switch checked={editForm.isActive} onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} color="inherit" disabled={editSaving}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            color="secondary"
            startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={editSaving}
            sx={{ textTransform: 'none' }}
          >
            {editSaving ? 'Saving...' : 'Update Leave Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const LeaveTypesPage: React.FC = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaveTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leaveApi.getLeaveTypes();
      setLeaveTypes(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to fetch leave types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  return (
    <Box>
      {/* ── Header row ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4" fontWeight="bold">
          Leave Types
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
          sx={{ textTransform: 'none' }}
        >
          Add Leave Type
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage leave categories and their configurations.
      </Typography>

      {/* ── Table directly on page ── */}
      <LeaveTypeConfig
        leaveTypes={leaveTypes}
        loading={loading}
        error={error}
        onRefresh={fetchLeaveTypes}
      />

      {/* ── Add dialog ── */}
      <AddLeaveTypeDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSaved={fetchLeaveTypes}
      />
    </Box>
  );
};

export default LeaveTypesPage;